import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { employeeLogin } from '../../store/authSlice';

const logoImage = '/assets/logo.png';
const vdaImage  = '/assets/banner.png';

const NOC_STEPS = [
  { icon: '📝', num: '01', title: 'Apply Online',   desc: 'Submit NOC application with required documents' },
  { icon: '🏢', num: '02', title: 'Dept Review',    desc: 'Related department reviews your application' },
  { icon: '✅', num: '03', title: 'Final Approval', desc: 'Final authority approves and issues NOC' },
  { icon: '⬇️', num: '04', title: 'Download NOC',  desc: 'Download your approved NOC certificate' },
];

export default function LoginPage() {
  const [form, setForm]         = useState({ login_id: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, role } = useSelector(s => s.auth);

  React.useEffect(() => {
    if (role === 'admin')                                       navigate('/admin/dashboard');
    else if (role === 'developer')                              navigate('/developer/dashboard');
    else if (role === 'related_dept' || role === 'final_dept')  navigate('/dept/dashboard');
  }, [role, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const res = await dispatch(employeeLogin(form));
    if (res.meta.requestStatus === 'fulfilled') {
      const r = res.payload.role;
      if (r === 'developer')  navigate('/developer/dashboard');
      else if (r === 'admin') navigate('/admin/dashboard');
      else                    navigate('/dept/dashboard');
    }
  };

  return (
    <div className="min-h-screen flex">

      {/* ── LEFT — Info Panel ─────────────────────────────── */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative flex-col"
        style={{ background: 'linear-gradient(135deg, #1a365d 0%, #2b6cb0 60%, #1e40af 100%)' }}>

        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />

        <div className="relative z-10 flex flex-col h-full p-10">

          {/* Banner Image */}
          <div className="rounded-2xl overflow-hidden shadow-2xl mb-8 flex-shrink-0" style={{ maxHeight: '260px' }}>
            <img src={vdaImage} alt="VDA" className="w-full h-full object-cover"
              onError={e => {
                e.target.parentNode.style.background = '#c53030';
                e.target.parentNode.innerHTML =
                  '<div class="flex flex-col items-center justify-center h-52 text-white">' +
                  '<span class="font-black text-4xl">VDA</span>' +
                  '<span class="text-base mt-2 font-semibold">NOC Management System</span>' +
                  '<span class="text-sm mt-1 opacity-80">Varanasi Development Authority</span>' +
                  '</div>';
              }} />
          </div>

          {/* How NOC Works */}
          <div className="mb-6">
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4 opacity-80">
              How NOC Application Works
            </h3>
            <div className="space-y-3">
              {NOC_STEPS.map((step, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center text-lg flex-shrink-0">
                    {step.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm">{step.title}</p>
                    <p className="text-blue-200 text-xs">{step.desc}</p>
                  </div>
                  <div className="w-7 h-7 bg-white/10 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {step.num}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Application Process */}
          <div className="mt-auto">
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4 opacity-80">
              Application Process
            </h3>
            <div className="flex gap-3">
              {NOC_STEPS.map((step, i) => (
                <div key={i} className="flex-1 bg-white/10 rounded-xl p-3 text-center">
                  <div className="text-2xl mb-1">{step.icon}</div>
                  <p className="text-white font-bold text-xs">{step.num}</p>
                  <p className="text-white font-semibold text-xs mt-0.5">{step.title}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-2">
              {NOC_STEPS.map((step, i) => (
                <div key={i} className="flex-1 text-center">
                  <p className="text-blue-200 text-xs leading-tight">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* ── RIGHT — Login Form ─────────────────────────────── */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 bg-gray-50">
        <div className="w-full max-w-sm">

          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg mx-auto mb-3 overflow-hidden">
              <img src={logoImage} alt="VDA" className="w-12 h-12 object-contain"
                onError={e => {
                  e.target.style.display = 'none';
                  e.target.parentNode.style.background = '#c53030';
                  e.target.parentNode.innerHTML = '<span style="color:white;font-weight:900;font-size:1.2rem">VDA</span>';
                }} />
            </div>
            <h1 className="text-xl font-bold text-gray-800">VDA NOC System</h1>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Welcome Back</h2>
              <p className="text-gray-500 text-sm mt-1">Employee / Official Login</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="form-label">Login ID</label>
                <input type="text" className="form-input" placeholder="Enter your login ID"
                  value={form.login_id} onChange={e => setForm(f => ({ ...f, login_id: e.target.value }))}
                  required autoFocus />
              </div>
              <div>
                <label className="form-label">Password</label>
                <div className="relative">
                  <input type={showPass ? 'text' : 'password'} className="form-input pr-10"
                    placeholder="Enter your password"
                    value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    required />
                  <button type="button" onClick={() => setShowPass(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPass ? '🙈' : '👁️'}
                  </button>
                </div>
                <div className="text-right mt-1.5">
                  <Link to="/forgot-password" className="text-xs text-blue-500 hover:text-blue-700 hover:underline">
                    Forgot Password?
                  </Link>
                </div>
              </div>
              <button type="submit" disabled={loading}
                className="w-full py-3 rounded-xl text-white font-semibold text-sm disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #1a365d, #2b6cb0)' }}>
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Signing in...
                  </span>
                ) : 'Sign In →'}
              </button>
            </form>

            <div className="mt-6 pt-5 border-t border-gray-100 text-center">
              <Link to="/citizen-login"
                className="block text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline">
                👥 Citizen Login (OTP) →
              </Link>
            </div>
          </div>

          <p className="text-center text-gray-400 text-xs mt-5">
            © {new Date().getFullYear()} Varanasi Development Authority
          </p>
        </div>
      </div>
    </div>
  );
}